// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MarginInfoParameters } from '@shared/commons/UtilsClasses/MarginInfo/MarginInfoParameters';
import { MarginInfoWrapper } from '@shared/commons/UtilsClasses/MarginInfo/MarginInfoWrapper';
import { QuoteValid } from '../Quotes/QuoteValid';
import { OperationType } from './OperationType';
import { OrderType } from './OrderType';
import { SlTpUtils } from './SlTpUtils';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
export class OrderEditRequestData {
    constructor() {
        this.instrument = null;
        this.account = null;
        this.quote = null;
        this.side = null;
        this.tif = null;
        this.cash = null;
        this.quantity = null;
        this.isCashMode = false;
        // TODO. Rename.
        this.parameterDict = {};
        this.placedFrom = null;
        this.disclosedQuantity = null;
        this.productType = null;
        this.leverageValue = null;
        this.comment = null;
        this.order = null;
        this.useStopLimitInsteadStop = null;
        this.limitPriceForStopLimitOrder = null;
        this.ocoCustomOrdersData = null;
        this.totalFee = null;
        this.estAvailableCash = null;
        // Тільки в PositionEdit використовує:
        this.position = null;
        this.canEditSLOrTrailingStop = null;
        this.canEditTP = null;
        this.isSLchanged = null;
        this.isTPchanged = null;
        this.UseSkipNoChange = null;
        this.userComment = null;
        this.isAllowOTHTrading = null;
    }
    TryGetProductType() {
        if (this.productType !== null) {
            return this.productType;
        }
        else if (!isNullOrUndefined(this.order) && this.order.ProductType !== null) {
            return this.order.ProductType;
        }
        else if (!isNullOrUndefined(this.position) && this.position.ProductType !== null) {
            return this.position.ProductType;
        }
        else {
            return null;
        }
    }
    static CreateFromOrder(order) {
        const result = new OrderEditRequestData();
        if (order != null) {
            result.instrument = order.Instrument;
            result.account = order.Account;
            result.side = order.BuySell;
            result.orderTypeId = order.OrderType;
            result.order = order;
        }
        return result;
    }
    GetMarginParameters() {
        const params = new MarginInfoParameters();
        params.account = this.account;
        params.instrument = this.instrument;
        params.orderType = this.orderTypeId;
        params.validity = this.tif;
        params.productType = this.TryGetProductType();
        params.leverageValue = this.leverageValue;
        params.isCashMode = this.isCashMode;
        if (params.isCashMode) {
            params.cash = this.cash;
        }
        else if (this.quantity != null) {
            params.amountLots = Quantity.toLots(this.quantity, this.instrument);
        }
        params.price = this.getPrice();
        params.stopPrice = this.getStopPrice();
        params.operation = this.side;
        params.isAllowOTHTrading = this.isAllowOTHTrading;
        return params;
    }
    /**
     * Makes a margin request and returns the Amount from the response.
     * @returns {Promise<number | null>} The amount from the margin request, or null if not found.
     */
    GetMarginAmount() {
        return __awaiter(this, void 0, void 0, function* () {
            const marginParameters = this.GetMarginParameters();
            const marginInfoWrapper = new MarginInfoWrapper();
            const amount = yield marginInfoWrapper.GetMarginAmount(marginParameters);
            return amount !== null && amount !== void 0 ? amount : null;
        });
    }
    getPrice() {
        var _a, _b;
        switch (this.orderTypeId) {
            case OrderType.Stop:
                return (_a = this.parameterDict) === null || _a === void 0 ? void 0 : _a.stopPrice;
            case OrderType.TrailingStop:
                return this.getTrailingStopPrice();
            default:
                return (_b = this.parameterDict) === null || _b === void 0 ? void 0 : _b.limitPrice;
        }
    }
    getStopPrice() {
        var _a;
        switch (this.orderTypeId) {
            case OrderType.StopLimit:
            case OrderType.OCO:
                return (_a = this.parameterDict) === null || _a === void 0 ? void 0 : _a.stopPrice;
            default:
                return null;
        }
    }
    getTrailingStopPrice() {
        var _a;
        const offsetVisual = (_a = this.parameterDict) === null || _a === void 0 ? void 0 : _a.trailingStop;
        const instrument = this.instrument;
        const isBuy = this.side === OperationType.Buy;
        if (isNullOrUndefined(instrument)) {
            return offsetVisual;
        }
        const lastQuote = instrument.GetLastQuote(QuoteValid.Last);
        if (isNullOrUndefined(lastQuote)) {
            return offsetVisual;
        }
        const price = isBuy ? lastQuote.Ask : lastQuote.Bid;
        const sign = isBuy ? 1 : -1;
        const ticks = SlTpUtils.toRealValue(offsetVisual, instrument, SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(instrument));
        return instrument.calculatePrice(price, ticks * sign, true);
    }
}
